/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { parseCompletedForm } from '../../forms/formHelpers';
import BorderlessButton from '../../common/buttons/BorderlessButton';

import BoardCard from './BoardCard';
import { getTemplateMap } from '../boards.helpers';

import useStatusCards from '../hooks/useStatusCards';

export default function BoardColumn({
  id,
  title,
  isLocked,
  showCardNumber,
  style = {},
  onAdd,
  onClick,
  onEdit,
  onForm,
  onShortList,
  displayFields = [],
  cardTemplate = {},
  userBoardPermissions,
  projectIdMap,
  equipmentIdMap,
  onBoardSelected,
  boardSortType = 'default',
  isDroppable,
}) {
  const draggableId = id ? id.toString() : 'null';

  const {
    canCreate,
  } = userBoardPermissions;

  const {
    cards,
    shadows,
  } = useStatusCards(id);

  const fullCards = useSelector((state) => state.boards.fullCards);

  const onAddClicked = useCallback(() => {
    onAdd(id);
  }, [id, onAdd]);
  const displaySet = useMemo(() => new Set(displayFields), [displayFields]);
  const fullTitle = `${title} ${cards.length}`;

  const templateMap = useMemo(() => getTemplateMap(cardTemplate), [cardTemplate]);

  const getCardView = useCallback((card) => {
    const { data = [] } = card;
    const fullData = parseCompletedForm({
      sections: data,
      templateMap,
    });
    return (
      <BoardCard
        key={card.id}
        {...fullCards[card.id] ?? card ?? {}}
        onClick={onClick}
        isLocked={isLocked}
        showCardNumber={showCardNumber}
        displayFields={displaySet}
        data={fullData}
        onEdit={onEdit}
        onForm={onForm}
        onShortList={onShortList}
        userBoardPermissions={userBoardPermissions}
        projectIdMap={projectIdMap}
        equipmentIdMap={equipmentIdMap}
        onBoardSelected={onBoardSelected}
        boardSortType={boardSortType}
      />
    );
  }, [
    onClick,
    onEdit,
    isLocked,
    displaySet,
    templateMap,
    projectIdMap,
    onBoardSelected,
    fullCards,
    boardSortType,
    onShortList,
  ]);

  const cardViews = useMemo(() => (
    cards.map(getCardView)
  ), [cards, getCardView]);

  const shadowViews = useMemo(() => (
    shadows.map(getCardView)
  ), [shadows, getCardView]);

  let cardStyle = {};
  if (cardViews.length === 0) {
    cardStyle = shadows.length === 0
      ? { height: '100%' }
      : { minHeight: 1 };
  }
  return (
    <div className="boards-column" style={style}>
      <div className="boards-column-inner-container">
        <Row justify="center" className="boards-column-header">
          {fullTitle}
          {canCreate && (
            <BorderlessButton
              iconNode={<PlusOutlined />}
              style={{
                position: 'absolute',
                right: 3,
                width: 20,
                height: 20,
                padding: 0,
              }}
              onClick={onAddClicked}
            />
          )}
        </Row>
        <div
          style={{
            height: 'calc(100% - 23px)',
            width: '100%',
            overflowY: 'auto',
          }}
        >
          <Droppable
            droppableId={draggableId}
            type="CARDS"
            direction="vertical"
            isDropDisabled={!isDroppable}
          >
            {({ droppableProps, innerRef, placeholder }) => (
              <div
                {...droppableProps}
                ref={innerRef}
                style={cardStyle}
              >
                {cardViews}
                {shadowViews}
                {placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
BoardColumn.propTypes = {
  id: PropTypes.number,
  title: PropTypes.string,
  isLocked: PropTypes.number,
  style: PropTypes.shape({
    width: PropTypes.string,
  }),
  onAdd: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onForm: PropTypes.func,
  onShortList: PropTypes.func,
  displayFields: PropTypes.arrayOf(PropTypes.string),
  cardTemplate: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.string,
      })),
    })),
  }),
  userBoardPermissions: PropTypes.shape({
    canCreate: PropTypes.bool,
  }),
  showCardNumber: PropTypes.number,
  projectIdMap: PropTypes.object,
  equipmentIdMap: PropTypes.object,
  onBoardSelected: PropTypes.func.isRequired,
  boardSortType: PropTypes.string,
  isDroppable: PropTypes.bool,
};

BoardColumn.defaultProps = {
  id: null,
  title: '',
  isLocked: 0,
  style: {},
  onAdd: null,
  onClick: null,
  onEdit: null,
  onForm: null,
  displayFields: [],
  cardTemplate: {},
  userBoardPermissions: {
    canCreate: false,
  },
  showCardNumber: false,
  projectIdMap: {},
  equipmentIdMap: {},
  boardSortType: 'default',
  isDroppable: true,
};
